import { Box, Card, Typography, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useState } from "react";
import chromeIcon from "assets/images/webgl/chrome_icon.svg";
import edgeIcon from "assets/images/webgl/edge_icon.svg";
import safariIcon from "assets/images/webgl/safari_icon.svg";
import plusIcon from "assets/images/webgl/plus_browser.svg";
import afinando_webgl from "assets/images/webgl/afinando_webgl.svg";
import download_file from "assets/images/webgl/download_file.svg";
import icon_suporte from "assets/images/webgl/icon_suporte.svg";
import AButton from "components/UI/Inputs/MUI/ATextField/AButton";
import WebGlDoneButton from "./WebGlDoneButton";

const WebGlTutorial = ({ onBypass }) => {
  const [step, setStep] = useState("select");
  const [copiedIndex, setCopiedIndex] = useState(null);

  const browsersConfig = [
    {
      name: "Google Chrome",
      icon: chromeIcon,
      tutorial: "chrome",
      videoUrl: "https://www.youtube.com/embed/VXihZ0FQwNU",
      type: "step",
      steps: [
        {
          text: (
            <>
              Na barra de endereço, digite{" "}
              <Tooltip
                title={copiedIndex ? "Copiado!" : "Clique para copiar"}
                arrow
                open={copiedIndex ? true : undefined}
              >
                <Box
                  component="span"
                  onClick={() => {
                    navigator.clipboard.writeText("chrome://settings/system");
                    setCopiedIndex("chrome://settings/system");
                    setTimeout(() => setCopiedIndex(null), 1500);
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  <a>chrome://settings/system</a>
                </Box>
              </Tooltip>
            </>
          ),
        },
        {
          text: "Ative a opção 'Usar aceleração de hardware quando disponível'",
        },
        {
          text: (
            <>
              Novamente na barra de endereço, digite{" "}
              <Tooltip
                title={copiedIndex ? "Copiado!" : "Clique para copiar"}
                arrow
                open={copiedIndex ? true : undefined}
              >
                <Box
                  component="span"
                  onClick={() => {
                    navigator.clipboard.writeText("chrome://flags");
                    setCopiedIndex("chrome://flags");
                    setTimeout(() => setCopiedIndex(null), 1500);
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  <a>chrome://flags</a>
                </Box>
              </Tooltip>
            </>
          ),
        },
        {
          text: "Na página de flags, procure por WEBGL",
        },
        {
          text: "Ative as opções de WebGL disponíveis em seu navegador",
        },
        {
          text: "Após ativar as opções, reinicie seu navegador para que as alterações sejam aplicadas",
        },
      ],
    },
    {
      name: "Microsoft Edge",
      icon: edgeIcon,
      tutorial: "edge",
      type: "step",
      videoUrl: "https://www.youtube.com/embed/2flFqSj_WBA",
      steps: [
        {
          text: (
            <>
              Na barra de endereço, digite{" "}
              <Tooltip
                title={copiedIndex ? "Copiado!" : "Clique para copiar"}
                arrow
                open={copiedIndex ? true : undefined}
              >
                <Box
                  component="span"
                  onClick={() => {
                    navigator.clipboard.writeText("edge://settings/system");
                    setCopiedIndex("edge://settings/system");
                    setTimeout(() => setCopiedIndex(null), 1500);
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  <a>edge://settings/system</a>
                </Box>
              </Tooltip>
            </>
          ),
        },
        {
          text: "Clique na opção ‘Sistema’",
        },
        {
          text: "Ative a opção ‘Usar aceleração gráfica quando disponível’",
        },
        {
          text: (
            <>
              Novamente na barra de pesquisa, digite{" "}
              <Tooltip
                title={copiedIndex ? "Copiado!" : "Clique para copiar"}
                arrow
                open={copiedIndex ? true : undefined}
              >
                <Box
                  component="span"
                  onClick={() => {
                    navigator.clipboard.writeText("edge://flags/");
                    setCopiedIndex("edge://flags/");
                    setTimeout(() => setCopiedIndex(null), 1500);
                  }}
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  <a>edge://flags/</a>
                </Box>
              </Tooltip>
            </>
          ),
        },
        {
          text: "Na página de flags, procure por WEBGL",
        },
        {
          text: "Habilite a opção de WebGL disponível",
        },
        {
          text: "Após habilitar as opções, reinicie seu navegador para que as alterações sejam aplicadas",
        },
      ],
    },
    {
      name: "Safari",
      icon: safariIcon,
      type: "others",
    },
    {
      name: "Outros",
      icon: plusIcon,
      type: "others",
    },
  ];

  const recomendationConfig = [
    {
      name: "Google Chrome",
      icon: chromeIcon,
      url: "https://www.google.com/intl/pt-PT/chrome/",
    },
    {
      name: "Microsoft Edge",
      icon: edgeIcon,
      url: "https://www.microsoft.com/edge",
    },
  ];

  const navigate = (url) => {
    window.open(url, "_blank");
  };

  const renderSteps = (config) => {
    return (
      <Grid
        container
        size={{ xs: 11, md: 8 }}
        spacing={2}
        sx={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#fafafa",
          borderRadius: 3,
          boxShadow: 1,
          p: 2,
          pt: 8,
          pb: 8,
        }}
      >
        <Grid
          item
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", textAlign: "center" }}
            gutterBottom
          >
            Como habilitar o WebGL no {config.name}
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ textAlign: "center" }}>
            Siga os passos abaixo ou assista o vídeo para habilitar o WebGL.
          </Typography>
        </Grid>
        <Grid
          container
          spacing={2}
          size={{ xs: 12, md: 10 }}
          sx={{
            mt: 1,
            justifyContent: "center",
            alignItems: "stretch",
            display: "flex",
          }}
        >
          <Grid
            item
            size={{ xs: 12, md: 6 }}
            sx={{
              display: "flex",
              boxShadow: 1,
              borderRadius: 3,
              overflow: "hidden",
              height: { xs: "220px", md: "360px" },
            }}
          >
            <iframe
              src={config.videoUrl}
              title="YouTube video player"
              style={{
                width: "100%",
                height: "100%",
                border: 0,
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Grid>
          <Grid
            item
            size={{ xs: 12, md: 6 }}
            sx={{
              backgroundColor: "#F0F3FF",
              boxShadow: 1,
              borderRadius: 3,
              p: 2,
              height: { xs: "220px", md: "360px" },
              alignContent: "center",
            }}
          >
            <Grid
              container
              spacing={2}
              sx={{
                p: 2,
                overflowY: "auto",
                height: { xs: "200px", md: "320px" },
              }}
            >
              {config.steps.map((step, index) => (
                <React.Fragment key={index}>
                  <Grid
                    item
                    size={{ xs: 12, md: 2 }}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "100%",
                        backgroundColor: "primary.light",
                        width: 34,
                        height: 34,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="white"
                        sx={{ fontWeight: "bold" }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item size={{ xs: 12, md: 10 }}>
                    <Typography variant="body1" component="span">
                      {step.text}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Grid>

          <Grid
            item
            size={3}
            sx={{ display: "flex", justifyContent: "start", mt: 2 }}
          >
            <AButton
              onClick={() => setStep("select")}
              size="large"
              sx={{ borderRadius: 3, fontWeight: "bold" }}
            >
              <Typography variant="h6" color="primary.main">
                VOLTAR
              </Typography>
            </AButton>
          </Grid>

          <Grid item size={6} sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <WebGlDoneButton onClick={onBypass} />
          </Grid>

          <Grid
            item
            size={3}
            sx={{ display: "flex", justifyContent: "end", mt: 2 }}
          >
            <AButton
              onClick={() =>
                navigate(
                  "https://afinandoocerebro.zohodesk.com/portal/pt-br/newticket",
                )
              }
              size="large"
              sx={{ borderRadius: 3, fontWeight: "bold" }}
              endIcon={
                <img
                  style={{ maxWidth: 20 }}
                  src={icon_suporte}
                  alt="Suporte"
                />
              }
            >
              <Typography variant="h6" color="primary.main">
                SUPORTE
              </Typography>
            </AButton>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderRecomendation = () => {
    return (
      <Grid
        container
        size={{ xs: 11, md: 8 }}
        spacing={2}
        sx={{
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#fafafa",
          borderRadius: 3,
          boxShadow: 1,
          p: 2,
          pt: 8,
        }}
      >
        <Grid item size={12} sx={{ display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={download_file}
            alt="Navegadores Recomendados"
            width="100%"
            maxWidth="90px"
          />
        </Grid>
        <Grid
          item
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", textAlign: "center" }}
            gutterBottom
          >
            Navegadores Recomendados
          </Typography>
          <Typography variant="body1" gutterBottom sx={{ textAlign: "center" }}>
            Alguns navegadores não oferecem suporte aos recursos necessários
            para rodar este jogo. <br />
            Recomendamos utilizar uma das opções abaixo.
          </Typography>
        </Grid>
        <Grid
          item
          container
          size={{ xs: 10, md: 8 }}
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#faf7fd",
            border: "2px solid",
            borderColor: "primary.main",
            borderRadius: 3,
            p: 2,
            pb: 4,
          }}
        >
          <Grid
            item
            size={12}
            color="primary.main"
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", textAlign: "center" }}
              gutterBottom
            >
              Escolha um navegador para baixar:
            </Typography>
          </Grid>
          {recomendationConfig.map((browser) => (
            <Grid
              item
              size={{ xs: 12, md: 3 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              key={browser.name}
              onClick={() => navigate(browser.url)}
            >
              <Box
                component="img"
                src={browser.icon}
                alt={browser.name}
                width="100%"
                maxWidth="50px"
              />
              <Box>{browser.name}</Box>
            </Grid>
          ))}
        </Grid>
        <Grid
          item
          size={6}
          sx={{ display: "flex", justifyContent: "end", mt: 2 }}
        >
          <AButton
            onClick={() => setStep("select")}
            size="large"
            sx={{ borderRadius: 3, fontWeight: "bold" }}
          >
            <Typography variant="h6" color="primary.main">
              VOLTAR
            </Typography>
          </AButton>
        </Grid>
        <Grid
          item
          size={6}
          sx={{ display: "flex", justifyContent: "start", mt: 2 }}
        >
          <AButton
            onClick={() =>
              navigate(
                "https://afinandoocerebro.zohodesk.com/portal/pt-br/newticket",
              )
            }
            size="large"
            sx={{ borderRadius: 3, fontWeight: "bold" }}
            endIcon={
              <img style={{ maxWidth: 20 }} src={icon_suporte} alt="Suporte" />
            }
          >
            <Typography variant="h6" color="primary.main">
              SUPORTE
            </Typography>
          </AButton>
        </Grid>
      </Grid>
    );
  };

  const renderContent = () => {
    if (step === "select") {
      return (
        <Grid
          container
          size={{ xs: 11, md: 8 }}
          spacing={2}
          sx={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#fafafa",
            borderRadius: 3,
            boxShadow: 1,
            p: 2,
            pt: 8,
          }}
        >
          <Grid
            item
            size={12}
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Box
              component="img"
              src={afinando_webgl}
              alt="Afinando WebGL"
              width="100%"
              maxWidth="200px"
            />
          </Grid>
          <Grid
            item
            size={12}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", textAlign: "center" }}
              gutterBottom
            >
              Encontramos um problema nas configurações do seu computador
            </Typography>
            <Typography
              variant="body1"
              gutterBottom
              sx={{ textAlign: "center" }}
            >
              Este jogo precisa do recurso{" "}
              <b>
                <a>WebGL</a>
              </b>{" "}
              para funcionar, mas ele não está ativo em seu navegador. <br />
              Vamos te ajudar a resolver!
            </Typography>
          </Grid>
          <Grid
            item
            container
            size={{ xs: 10, md: 8 }}
            sx={{
              display: "flex",
              justifyContent: "center",
              backgroundColor: "#faf7fd",
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: 3,
              p: 2,
              mb: 6,
              pb: 4,
            }}
          >
            <Grid
              item
              size={12}
              color="primary.main"
              sx={{ display: "flex", justifyContent: "center" }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", textAlign: "center" }}
                gutterBottom
              >
                Selecione o navegador em uso:
              </Typography>
            </Grid>
            {browsersConfig.map((browser) => (
              <Grid
                item
                size={{ xs: 12, md: 3 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
                key={browser.name}
                onClick={() => setStep(browser.tutorial)}
              >
                <Box
                  component="img"
                  src={browser.icon}
                  alt={browser.name}
                  width="100%"
                  maxWidth="50px"
                />
                <Box>{browser.name}</Box>
              </Grid>
            ))}
          </Grid>
        </Grid>
      );
    }
    if (step === "chrome") {
      return renderSteps(browsersConfig.find((b) => b.tutorial === "chrome"));
    }
    if (step === "edge") {
      return renderSteps(browsersConfig.find((b) => b.tutorial === "edge"));
    }
    return renderRecomendation();
  };
  return (
    <Grid
      container
      spacing={2}
      sx={{
        backgroundColor: "secondary.light",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: 2,
        minHeight: "100vh",
      }}
    >
      {renderContent()}
    </Grid>
  );
};

export default WebGlTutorial;
